import React, { useState, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    Stack,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    alpha,
    Tooltip
} from '@mui/material';
import { Print as PrintIcon, FileDownload as DownloadIcon, Warning as WarningIcon } from '@mui/icons-material';
import { formatCurrency } from '../../../utils/compteClientEngine';
import { COLORS, jsonToXlsx } from '../../../settings/themeSettings';

const ReleveCompteViewer = ({ data, statusFilter, onChangeStatusFilter }) => {
    const [activeRowIdx, setActiveRowIdx] = useState(0);
    const tableRef = useRef(null);

    if (!data) return null;

    const {
        clientName = 'Client',
        dateSituationStr = 'Ce jour',
        rows = [],
        totalFacture = 0,
        totalRegle = 0,
        totalSoldeDu = 0
    } = data;

    const dateTirage = new Date().toLocaleDateString('fr-FR') + ' à ' + new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveRowIdx(prev => Math.min(rows.length - 1, prev + 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveRowIdx(prev => Math.max(0, prev - 1));
        }
    };

    const handleExportExcel = () => {
        const exportData = rows.map(r => ({
            'N° Facture': r.numeroFacture,
            'Date facture': r.dateFacture,
            'Échéance': r.echeance,
            'Montant facture (CFA)': r.montantFacture,
            'Montant réglé (CFA)': r.montantRegle,
            'Solde dû (CFA)': r.soldeDu
        }));
        jsonToXlsx(exportData, `Releve_Compte_${clientName.replace(/\s+/g, '_')}`);
    };

    const handlePrint = () => {
        window.print();
    };

    return (
        <Box tabIndex={0} ref={tableRef} onKeyDown={handleKeyDown} sx={{ outline: 'none' }}>
            {/* TOOLBAR & FILTRES ÉCRAN (SECTION 5.4) */}
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography variant="body2" fontWeight="bold" color="text.secondary">
                        Filtre affichage :
                    </Typography>
                    <RadioGroup
                        row
                        value={statusFilter}
                        onChange={(e) => onChangeStatusFilter && onChangeStatusFilter(e.target.value)}
                    >
                        <FormControlLabel value="ALL" control={<Radio size="small" />} label="Factures ouvertes" />
                        <FormControlLabel value="TOUTES" control={<Radio size="small" />} label="Toutes" />
                        <FormControlLabel value="IMPAYEE" control={<Radio size="small" />} label="Impayées" />
                        <FormControlLabel value="PAYEE" control={<Radio size="small" />} label="Payées" />
                    </RadioGroup>
                </Stack>

                <Stack direction="row" spacing={1}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        onClick={handlePrint}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Imprimer
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportExcel}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Export Excel
                    </Button>
                </Stack>
            </Stack>

            {/* DOCUMENT OFFICIEL IMPRIMABLE (SECTION 5.4) */}
            <Paper
                elevation={0}
                className="printable-document"
                sx={{
                    p: 3,
                    border: '2px solid #0f172a',
                    borderRadius: '8px',
                    bgcolor: 'white',
                    maxWidth: '1000px',
                    mx: 'auto'
                }}
            >
                {/* EN-TÊTE TRANSVERSAL COMMUN */}
                <Box sx={{ borderBottom: '2px solid #0f172a', pb: 1.5, mb: 2 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 2fr 1.2fr', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="body1" fontWeight="bold" sx={{ color: '#0f172a' }}>
                                AFRICA FOR TOURISM
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Agence de Voyages & Tourisme
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                TravelManager v2.0
                            </Typography>
                        </Box>

                        <Box textAlign="center">
                            <Typography variant="h5" fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: 1, color: '#0f172a' }}>
                                Relevé de compte
                            </Typography>
                        </Box>

                        <Box textAlign="right">
                            <Typography variant="caption" display="block">
                                <strong>À la date du :</strong> {dateSituationStr}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                                Date de tirage : {dateTirage}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                                Page : 1 / 1
                            </Typography>
                        </Box>
                    </Box>

                    {/* BANDEAU CLIENT */}
                    <Box sx={{ mt: 1.5, p: 1, bgcolor: '#0f172a', color: 'white', borderRadius: '4px', fontWeight: 'bold' }}>
                        Client : {clientName}
                    </Box>
                </Box>

                {/* BANDEAU TOTAL DÛ FIGÉ EN HAUT D'ÉCRAN (EXIGENCE SECTION 5.4) */}
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight="bold" color="#0f172a">
                        TOTAL DÛ DU CLIENT (Situation au {dateSituationStr}) :
                    </Typography>
                    <Typography variant="h6" fontWeight="900" sx={{ color: totalSoldeDu > 0 ? '#dc2626' : '#16a34a' }}>
                        {formatCurrency(totalSoldeDu)} CFA
                    </Typography>
                </Box>

                {/* TABLEAU DES FACTURES DU RELEVÉ */}
                <Box sx={{ border: '1px solid #0f172a', borderRadius: '4px', overflow: 'hidden' }}>
                    {/* Titres de colonnes figés */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '160px 130px 130px 150px 150px 160px', bgcolor: '#0f172a', color: 'white', p: 1, fontWeight: 'bold', fontSize: '0.85rem' }}>
                        <Box>N° Facture</Box>
                        <Box>Date facture</Box>
                        <Box>Échéance</Box>
                        <Box sx={{ textAlign: 'right' }}>Montant facture</Box>
                        <Box sx={{ textAlign: 'right' }}>Montant réglé</Box>
                        <Box sx={{ textAlign: 'right' }}>Solde dû</Box>
                    </Box>

                    <Box sx={{ maxHeight: '55vh', overflowY: 'auto' }}>
                        {rows.length === 0 ? (
                            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                                Aucune facture correspondant aux critères à la date du {dateSituationStr}.
                            </Box>
                        ) : (
                            rows.map((r, idx) => {
                                const isSelected = activeRowIdx === idx;
                                const isAvoir = r.soldeDu < 0;

                                return (
                                    <Box
                                        key={r.id || idx}
                                        onClick={() => setActiveRowIdx(idx)}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: '160px 130px 130px 150px 150px 160px',
                                            p: 1,
                                            alignItems: 'center',
                                            fontSize: '0.85rem',
                                            borderBottom: '1px solid #e2e8f0',
                                            bgcolor: isSelected ? alpha(COLORS.primary, 0.12) : (idx % 2 === 0 ? 'white' : '#f8fafc'),
                                            borderLeft: isSelected ? `4px solid ${COLORS.primary}` : '4px solid transparent',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <Box sx={{ fontWeight: 700, color: COLORS.primary }}>{r.numeroFacture}</Box>
                                        <Box sx={{ color: 'text.secondary' }}>{r.dateFacture}</Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <span>{r.echeance}</span>
                                            {r.isLate && (
                                                <Tooltip title={`En retard de ${r.daysLate} jours`}>
                                                    <WarningIcon sx={{ fontSize: 16, color: '#dc2626' }} />
                                                </Tooltip>
                                            )}
                                        </Box>
                                        <Box sx={{ textAlign: 'right', fontWeight: 600 }}>
                                            {formatCurrency(r.montantFacture)}
                                        </Box>
                                        <Box sx={{ textAlign: 'right', fontWeight: 600, color: r.montantRegle > 0 ? '#16a34a' : 'inherit' }}>
                                            {formatCurrency(r.montantRegle)}
                                        </Box>
                                        <Box sx={{ textAlign: 'right', fontWeight: 800, color: isAvoir ? '#2563eb' : (r.isLate ? '#dc2626' : '#0f172a') }}>
                                            {formatCurrency(r.soldeDu)}
                                        </Box>
                                    </Box>
                                );
                            })
                        )}
                    </Box>

                    {/* LIGNE DES TOTAUX (SECTION 5.4) */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '160px 130px 130px 150px 150px 160px',
                            bgcolor: '#f1f5f9',
                            p: 1.2,
                            fontWeight: 'bold',
                            fontSize: '0.875rem',
                            borderTop: '2px solid #0f172a'
                        }}
                    >
                        <Box sx={{ gridColumn: 'span 3', textAlign: 'right', pr: 2 }}>
                            Totaux :
                        </Box>
                        <Box sx={{ textAlign: 'right', color: '#0f172a' }}>
                            {formatCurrency(totalFacture)}
                        </Box>
                        <Box sx={{ textAlign: 'right', color: '#16a34a' }}>
                            {formatCurrency(totalRegle)}
                        </Box>
                        <Box sx={{ textAlign: 'right', color: totalSoldeDu > 0 ? '#dc2626' : '#0f172a', fontWeight: 900 }}>
                            {formatCurrency(totalSoldeDu)} CFA
                        </Box>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default ReleveCompteViewer;
